import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLeasingUrlParams } from '../hooks';

import {
  Box,
  Button,
  Card,
  Heading,
  Icon,
  Image,
  List,
  Pagination,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import { IconHandshakeFilled, IconPlusFilled } from '@zillow/constellation-icons';
import { LoadingButton, LoadingHeading, LoadingText } from '@zg-rentals/loading-components';
import { LeaseStatus, useRentalManagerUser } from '@zg-rentals/rental-manager-env';

import { Lease, LeaseAction, Property } from '../models';
import {
  useIsOnWaitlistQuery,
  useLeaseSupportForPropertyQuery,
  usePaginatedLeasesForLandlordQuery,
  usePropertyInfoQuery,
  useWebAnalyticsForListingQuery,
} from '../utils/queries';
import type { LeaseBundleDto } from '@zg-rentals/ts-leasing-client';
import RegionWaitlistUpsell from '../components/Widgets/RegionWaitlistUpsell';
import LeaseRow from './LandlordLeasesDashboard/LeaseRow';
import { PAGINATION_NUMBER_OF_RESULTS } from '../constants/stringConstants';
import ManageYourLeaseUpsell, { shouldShowManageLeaseUpsell } from '../components/Widgets/ManageYourLeaseUpsell';
import { emitCreateLeaseStartLeaseEvent } from '../../analytics/events';
import { useIsInMobileWebView } from '../utils/mobileHandoffFunctions';
import DocumentSignature from '../../../images/documentSignature.svg';

export function LandlordLeasesDashboard() {
  const isInMobileWebView = useIsInMobileWebView();
  const { user } = useRentalManagerUser();
  const history = useHistory();
  const { propertyId } = useLeasingUrlParams();
  const [pageNumber, setPageNumber] = useState(0);

  const leaseSupportQuery = useLeaseSupportForPropertyQuery(propertyId);
  const waitlistQuery = useIsOnWaitlistQuery(propertyId);
  const paginatedLeasesForLandlordQuery = usePaginatedLeasesForLandlordQuery({
    listingAlias: propertyId,
    numResults: PAGINATION_NUMBER_OF_RESULTS,
    offset: pageNumber * PAGINATION_NUMBER_OF_RESULTS,
  });

  const propertyQuery = usePropertyInfoQuery(propertyId);
  const property = propertyQuery.data ? new Property(propertyQuery.data) : undefined;

  const webAnalyticsQuery = useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });
  const zrm_listing_info = webAnalyticsQuery.data;

  const leaseSupportInfo = leaseSupportQuery.data;
  const isFullWizardSupported = leaseSupportInfo?.isSupported ?? false;
  const willBeSupportedInFuture = leaseSupportInfo?.willBeSupportedInFuture ?? false;
  const isOnWaitlist = waitlistQuery.data?.isPresent ?? false;
  const showWaitlistBox = !isFullWizardSupported && !isOnWaitlist && willBeSupportedInFuture;

  const leaseBundles: Array<LeaseBundleDto> = paginatedLeasesForLandlordQuery.data?.leases || [];
  const leasesForProperty = leaseBundles
    .map((leaseDto) => new Lease(leaseDto))
    .sort((leaseA, leaseB) => Lease.sortByLastUpdatedFirst(leaseA, leaseB));

  const leaseActionsByLeaseId: Record<number, Array<LeaseAction>> = {};
  leaseBundles.forEach((bundle) => {
    leaseActionsByLeaseId[bundle.id] = (bundle.actions || []).map(
      (actionDto) => new LeaseAction(actionDto.name, actionDto.disabled),
    );
  });

  const totalNumberOfLeases = paginatedLeasesForLandlordQuery.data?.totalNumberOfLeases || 0;
  const leasesFetched = paginatedLeasesForLandlordQuery.isSuccess;
  const hasEverFetchedLeases = !!paginatedLeasesForLandlordQuery.data;

  function onCreateLease() {
    history.push(Property.getCombinedOnboardingPath(propertyId));
    const zrmLeaseInfo = {
      lease_type_cd: null,
      lease_status_txt: LeaseStatus.INCOMPLETE_DRAFT,
      lease_bundle_id: null,
    };
    emitCreateLeaseStartLeaseEvent({ user, listing: zrm_listing_info, zrmLeaseInfo });
  }

  function renderLeaseCardSkeletons() {
    return (
      <>
        {Array.from({ length: 5 }).map((_, index) => (
          <Card key={index} css={{ marginBottom: 'tight', padding: 'loose' }} elevated={false} tone="neutral">
            <Box
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 'tight',
                flexDirection: { base: 'row', mdDown: 'column' },
              }}
            >
              <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tightest', flex: 1 }}>
                <LoadingHeading isLoading level={3} css={{ textStyle: 'responsive.heading-sm' }} loadingWidth="15rem">
                  Loading
                </LoadingHeading>
                <Box css={{ display: 'flex', gap: 'tighter' }}>
                  <LoadingText isLoading loadingWidth="10rem">
                    Loading
                  </LoadingText>
                  <LoadingText isLoading loadingWidth="10rem">
                    Loading
                  </LoadingText>
                </Box>
                <LoadingText isLoading loadingWidth="15rem">
                  Loading
                </LoadingText>
              </Box>
              <Box css={{ display: 'flex', gap: 'tighter', alignItems: 'flex-start' }}>
                <LoadingButton isLoading loadingWidth="10rem">
                  Action
                </LoadingButton>
              </Box>
            </Box>
          </Card>
        ))}
      </>
    );
  }

  function renderUpsells() {
    const areUpsellQueriesLoaded = leaseSupportQuery.isSuccess && waitlistQuery.isSuccess && leasesFetched;

    if (!areUpsellQueriesLoaded) {
      return null;
    }

    const firstLeaseEligibleToManageLease = leasesForProperty.filter((lease) => {
      return lease.isEligibleForLeaseRenewal() && !lease.latestExtension && shouldShowManageLeaseUpsell(lease);
    })[0];

    return (
      <>
        {isInMobileWebView && <Box css={{ marginTop: 'loose' }} />}
        {showWaitlistBox && (
          <Box css={{ marginBottom: 'tight' }}>
            <RegionWaitlistUpsell />
          </Box>
        )}
        {firstLeaseEligibleToManageLease && (
          <Box css={{ marginBottom: 'tight' }}>
            <ManageYourLeaseUpsell lease={firstLeaseEligibleToManageLease} />
          </Box>
        )}
      </>
    );
  }

  function renderHeading(): string {
    const firstName = user?.firstName;

    return `Hi${firstName ? ` ${firstName}` : ''}, ready to ${isFullWizardSupported ? 'create' : 'upload'} your lease?`;
  }

  function renderLeases() {
    const numberOfPages = Math.ceil(totalNumberOfLeases / PAGINATION_NUMBER_OF_RESULTS);

    return (
      <>
        {renderUpsells()}
        {leasesForProperty.length > 0 || !leasesFetched ? (
          <>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'tight',
                gap: 'tighter',
              }}
            >
              <LoadingHeading
                isLoading={!leasesFetched && !hasEverFetchedLeases}
                level={2}
                css={{ textStyle: 'responsive.heading-sm' }}
                loadingWidth="10rem"
              >
                Your leases
              </LoadingHeading>
              <LoadingButton
                isLoading={!leasesFetched && !hasEverFetchedLeases}
                tone="brand"
                emphasis="outlined"
                size="md"
                icon={<IconPlusFilled />}
                iconPosition="start"
                onClick={onCreateLease}
                data-test="create-new-lease"
                loadingWidth="10rem"
              >
                Create a lease
              </LoadingButton>
            </Box>
            {!leasesFetched && !hasEverFetchedLeases
              ? renderLeaseCardSkeletons()
              : leasesForProperty.map((lease: Lease, index: number) => (
                  <Box key={`${lease.id}`} css={{ marginBottom: 'tight' }}>
                    <LeaseRow {...{ index, lease, property }} leaseActions={leaseActionsByLeaseId[lease.id] || []} />
                  </Box>
                ))}
            {hasEverFetchedLeases && numberOfPages > 1 && (
              <Pagination
                currentPageIndex={pageNumber}
                totalPages={numberOfPages}
                onPageSelected={(pageIndex) => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setPageNumber(pageIndex);
                }}
              />
            )}
          </>
        ) : (
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tight' }}>
            <Box css={{ gridColumn: { base: 'span 12', md: 'span 8' } }}>
              <Image src={DocumentSignature} css={{ height: '160px', width: '160px' }} alt="block of buildings" />
              <Heading
                level={2}
                css={{ textStyle: 'responsive.heading-md', marginTop: 'tight', marginBottom: 'loose' }}
              >
                {renderHeading()}
              </Heading>
              <Heading level={3} css={{ textStyle: 'responsive.body-lg-bold', marginBottom: 'loose' }}>
                {isFullWizardSupported
                  ? 'Now you can create a lease that includes legal clauses specific to your area'
                  : 'Upload your lease for signing or store a signed lease'}
              </Heading>
              <Paragraph css={{ marginBottom: 'loose' }}>
                {isFullWizardSupported
                  ? 'You’ll use templates created with the help of local law firms, and your new lease can be ready to sign in about an hour.'
                  : 'Share a lease with your renters to sign online. Or store a signed lease on Zillow to keep all your property information in one place.'}
              </Paragraph>
              <Button tone="brand" emphasis="filled" size="md" onClick={onCreateLease}>
                Create a lease
              </Button>
            </Box>
            <Box css={{ gridColumn: { base: 'span 12', md: 'span 4' } }}>
              <Card elevated={false} tone="neutral" css={{ padding: 'loose' }}>
                <Icon size="lg" role="img" css={{ marginBottom: 'tight' }}>
                  <IconHandshakeFilled />
                </Icon>
                <Heading level={4} css={{ textStyle: 'responsive.body-lg-bold', marginBottom: 'tighter' }}>
                  Everything you need to manage your lease
                </Heading>
                <List.Root>
                  {isFullWizardSupported && (
                    <List.Item>
                      Templates that were created by Zillow, together with law firms, specific to your area
                    </List.Item>
                  )}
                  <List.Item>
                    {isFullWizardSupported
                      ? 'Upload an existing lease or customize a brand new Zillow one'
                      : 'Upload or store an existing lease'}
                  </List.Item>
                  <List.Item>
                    Send and sign lease agreements to renters electronically and skip the in-person meeting
                  </List.Item>
                  <List.Item>View all your leases in one place</List.Item>
                </List.Root>
                <TextButton asChild css={{ marginTop: 'tight' }}>
                  <a href="https://www.zillow.com/z/rental-manager/rental-lease-agreements/">Learn more</a>
                </TextButton>
              </Card>
            </Box>
          </Box>
        )}
      </>
    );
  }

  return <>{renderLeases()}</>;
}

export default LandlordLeasesDashboard;
